<script setup lang="ts">
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { nanoid } from "nanoid";
import type { DefaultInputProps, IMultiSelectItem } from "@/interfaces";
import { toRefs } from "vue";

const id = nanoid();

export type TagSelectorSize = "sm" | "md";

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  modelValue?: string;
  items: IMultiSelectItem[];
  allowDeselect?: boolean;
  size?: TagSelectorSize;
}

const props = withDefaults(defineProps<Props>(), {
  allowDeselect: true,
  size: "md",
});

const { modelValue, allowDeselect } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);

const handleSelect = (item: IMultiSelectItem) => {
  if (modelValue.value === item.id && allowDeselect.value) {
    emit("update:modelValue", undefined);
  } else {
    emit("update:modelValue", item.id);
  }
};

const SELECTOR_SIZE_MAP: Record<TagSelectorSize, string> = {
  sm: "py-2 px-4",
  md: "p-3 pr-4",
};

const SELECTOR_TITLE_STYLE_MAP: Record<TagSelectorSize, string> = {
  sm: "h200",
  md: "h300",
};
</script>

<template>
  <InputWrapper
    :forId="id"
    :label="label"
    :description="description"
    :isRequired="isRequired"
    :inputKey="inputKey"
    :tooltip="tooltip"
    :disabled="disabled"
    :name="name"
    :validation="validation"
    :errorMessageKey="errorMessageKey"
  >
    <div class="flex gap-x-[10px] gap-y-[14px] flex-wrap">
      <button
        type="button"
        role="radio"
        v-for="item of items"
        :key="item.id"
        class="focus-visible:outline-neutral1000 outline rounded-full flex items-center gap-2"
        :class="{
          'bg-white outline-neutral500': modelValue !== item.id,
          'bg-darkOcean100 outline-darkOcean600 outline-2':
            modelValue === item.id,
          [SELECTOR_SIZE_MAP[size]]: true,
        }"
        @click="() => handleSelect(item)"
      >
        <div v-if="item.icon" class="p-1 flex items-start justify-center">
          <i :class="item.icon" :style="{ color: item.iconBackground }"></i>
        </div>
        <div class="text-neutral1000" :class="SELECTOR_TITLE_STYLE_MAP[size]">
          {{ item.title }}
        </div>
      </button>
    </div>
  </InputWrapper>
</template>
